import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import i18n from "../../i18n";
import { City, CityDescription } from "../../interfaces/city";
import { Layer } from "../../interfaces/layer";
import { LayersDataTypes } from "../../types/layers-data-types";
import { GetQueryFeatures } from "../../utils/feature-service";
import { getEmassarData } from "../api";
import { EmassarMethod } from "../../types/emassar-method";
import { RootState } from "../store";
import { LayerType } from "../../types/layer-type";

interface CityState {
  searchValue: string;
  data: City[];
  openedCity: City | null;
  previousOpenedCity: City | null;
  openedFromMap: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: CityState = {
  searchValue: "",
  data: [],
  openedCity: null,
  previousOpenedCity: null,
  loading: false,
  error: null,
  openedFromMap: false,
};

export const fetchCitiesData = createAsyncThunk(
  "cities/fetchCitiesData",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state: any = getState();
      const layers: Layer[] = state.map.layers.filter(
        (layer: Layer) =>
          [
            LayersDataTypes.Plots,
            LayersDataTypes.InvestmentOpportunities,
            LayersDataTypes.SpacesForRent,
            LayersDataTypes.CityBoundary,
          ].includes(layer.dataType) &&
          layer.type === LayerType.ArcgisFeatureServer,
      );
      const tokens = state.map.arcgisTokens;
      const vacantPlots = new Map<number, number>();
      const vacantInvestments = new Map<number, number>();
      const vacantSpacesForRent = new Map<number, number>();
      const prodtypeMap = new Map<number, string[]>();
      const companies = new Map<number, number[]>();
      const cities = new Map<number, any[]>();
      for (const layer of layers) {
        const queryFeatures = await GetQueryFeatures(
          layer,
          ["estateID", "estateid"],
          tokens,
        );
        switch (layer.dataType) {
          case LayersDataTypes.Plots: {
            queryFeatures.features.forEach((feature) => {
              const estate = feature.attributes.estateID as number;
              if (feature.attributes.status?.toLowerCase() === "vacant") {
                if (!vacantPlots.has(estate)) vacantPlots.set(estate, 0);
                vacantPlots.set(estate, vacantPlots.get(estate)! + 1);
              }
              const prodtype = feature.attributes.prodtype_e;
              if (prodtype) {
                prodtypeMap.set(estate, [
                  ...(prodtypeMap.get(estate) ?? []),
                  prodtype,
                ]);
              }
              const tenant_id = feature.attributes.tenant_id;
              if (tenant_id) {
                companies.set(estate, [
                  ...(companies.get(estate) ?? []),
                  tenant_id,
                ]);
              }
            });
            break;
          }
          case LayersDataTypes.InvestmentOpportunities: {
            queryFeatures.features.forEach((feature) => {
              const estate = feature.attributes.estateID as number;
              if (feature.attributes.status?.toLowerCase() === "vacant") {
                if (!vacantInvestments.has(estate))
                  vacantInvestments.set(estate, 0);
                vacantInvestments.set(
                  estate,
                  vacantInvestments.get(estate)! + 1,
                );
              }
              const prodtype = feature.attributes.prodtype_e;
              if (prodtype) {
                prodtypeMap.set(estate, [
                  ...(prodtypeMap.get(estate) ?? []),
                  prodtype,
                ]);
              }
              const tenant_id = feature.attributes.tenant_id;
              if (tenant_id) {
                companies.set(estate, [
                  ...(companies.get(estate) ?? []),
                  tenant_id,
                ]);
              }
            });
            break;
          }
          case LayersDataTypes.SpacesForRent: {
            queryFeatures.features.forEach((feature) => {
              const estate = feature.attributes.estateid as number;
              if (feature.attributes.status?.toLowerCase() === "vacant") {
                if (!vacantSpacesForRent.has(estate))
                  vacantSpacesForRent.set(estate, 0);
                vacantSpacesForRent.set(
                  estate,
                  vacantSpacesForRent.get(estate)! + 1,
                );
              }
              const prodtype = feature.attributes.prodtype_e;
              if (prodtype) {
                prodtypeMap.set(estate, [
                  ...(prodtypeMap.get(estate) ?? []),
                  prodtype,
                ]);
              }
              const tenant_id = feature.attributes.tenant_id;
              if (tenant_id) {
                companies.set(estate, [
                  ...(companies.get(estate) ?? []),
                  tenant_id,
                ]);
              }
            });
            break;
          }
          case LayersDataTypes.CityBoundary: {
            queryFeatures.features.forEach((feature) => {
              const estate = feature.attributes.estateID as number;
              if (!estate) return;
              if (!cities.has(estate)) cities.set(estate, []);
              cities.set(estate, [
                ...cities.get(estate)!,
                { ...feature.attributes, layerId: layer.id },
              ]);
            });
            break;
          }
        }
      }
      const emassarCities: any[] =
        (await getEmassarData(
          EmassarMethod.Estate,
          Array.from(cities.keys()),
        )) || [];
      const result = Array.from(cities.entries()).map(([key, value]): City => {
        const area = Math.ceil(
          value.reduce((acc, item) => acc + item.Shape__Area, 0),
        );
        const nameEn = value[0].estateEn;
        const nameAr = value[0].estateAr;
        const description: CityDescription = {};
        const city = emassarCities.find((c) => c.estateId === key);
        if (city) {
          description.highway = city.nearest_major_highway_en ?? "";
          description.port =
            (city.nearest_port_facility_en ?? "") +
            (city.distance_to_port ? " " + city.distance_to_port + "km" : "");
          description.airport =
            (city.nearest_airport_en ?? "") +
            (city.distance_to_airport
              ? " " + city.distance_to_airport + "km"
              : "");
          description.sectorFocus = city.sector_focus_en ?? "";
          description.rentValue = city.rent_policy_en ?? "";
        }
        description.products = new Set(prodtypeMap.get(key) ?? []).size;
        description.companies = new Set(companies.get(key) ?? []).size;
        return {
          estateEn: key,
          nameEn: nameEn,
          nameAr: nameAr,
          layerId: value[0].layerId,
          totalArea: area,
          vacantPlots: vacantPlots.get(key),
          spacesForRent: vacantSpacesForRent.get(key),
          investmentPlots: vacantInvestments.get(key),
          description: description,
        };
      });
      return { data: result };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load companies data");
    }
  },
);
export const citiesSlice = createSlice({
  name: "cities",
  initialState,
  reducers: {
    setSearchValue(state, action: PayloadAction<string>) {
      state.searchValue = action.payload;
    },
    setEstate(state, action: PayloadAction<number>) {
      const city = state.data.find((c) => c.estateEn === action.payload);
      if (city) {
        if (!state.openedFromMap) {
          state.previousOpenedCity = state.openedCity;
        }
        state.openedCity = city;
      }
    },
    setOpenedCity(state, action: PayloadAction<City>) {
      if (state.openedCity) {
        state.previousOpenedCity = state.openedCity;
      }
      state.openedCity = action.payload;
    },
    clearOpenedCity(state) {
      if (state.openedFromMap) {
        state.openedCity = null;
        state.openedFromMap = false;
      } else {
        state.previousOpenedCity = null;
        state.openedCity = null;
      }
    },
    clearPreviousOpenedCity(state) {
      state.previousOpenedCity = null;
    },
    setOpenedFromMapCitiesCard(state, action: PayloadAction<boolean>) {
      state.openedFromMap = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCitiesData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCitiesData.fulfilled,
        (state, action: PayloadAction<{ data: City[] }>) => {
          state.data = action.payload.data.sort((a, b) =>
            a.nameEn.localeCompare(b.nameEn),
          );
          state.loading = false;
        },
      )
      .addCase(fetchCitiesData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setEstate,
  setSearchValue,
  setOpenedCity,
  clearOpenedCity,
  clearPreviousOpenedCity,
  setOpenedFromMapCitiesCard,
} = citiesSlice.actions;

export default citiesSlice.reducer;

const selectCompanies = (state: RootState) => state.cities;

export const selectFilteredCities = createSelector(
  [selectCompanies, () => i18n.language],
  (cities, language) => {
    const { data, searchValue } = cities;
    let filtered = data;

    if (searchValue.trim()) {
      const query = searchValue.trim().toLowerCase();
      filtered = filtered.filter((city) =>
        language === "ar"
          ? city.nameAr && city.nameAr.toLowerCase().includes(query)
          : city.nameEn.toLowerCase().includes(query),
      );
    }

    return filtered;
  },
);
